// src/Components/modules/Coordinacion/MenuContextualTraslado.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Menu, Typography } from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined,
  CarOutlined,
  CommentOutlined,
  AimOutlined,
  FileTextOutlined,
  PushpinOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import CerrarTrasladoDialog from "../Dialog/CerrarTrasladoDialog";
import ValidarTrasladoDialog from "../Dialog/ValidarTrasladoDialog";
import ObservacionesDialog from "../Dialog/ObservacionesDialog";
import HistorialTrasladoDialog from "../Dialog/HistorialTrasladoDialog";
import VerTrasladoDialog from "../Dialog/VerTrasladoDialog";
import DesasignarMovilDialog from "../Dialog/DesasignarMovilDialog";
import AsignarMovilDialog from "../Dialog/AsignarMovilDialog";
import ConfirmarTrasladoDialog from "../Dialog/ConfirmarTrasladoDialog";
import DesconfirmarTrasladoDialog from "../Dialog/DesconfirmarTrasladoDialog";
import DesvalidarTrasladoDialog from "../Dialog/DesvalidarTrasladoDialog";
import ReiniciarTrasladoDialog from "../Dialog/ReiniciarTrasladoDialog";
import { message } from "antd";

import MapDialog from "../CenterControl/MapDialog";
import RutaTrazadaView from "../ControlServicios/RutaTrazadaView";
import CorregirCoordenadasDialog from "../Dialog/CorregirCoordenadasDialog";
import ActualizarOrigenDestinoDialog from "../Dialog/ActualizarOrigenDestinoDialog";
import FuecTrasladoDialog from "../Fuec/FuecTrasladoDialog";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";

function MenuContextualTraslado({
  contextMenu,
  handleCloseContextMenu,
  handleOptionClick,
  trasladoId,
  onEstadoCambiado,
  origin,
  destination,
  traslado,
  selectedTraslados,
  onAsignarMultiple,
  selectedMovil,
  selectedStatus,
  onOrigenDestinoActualizado,
}) {
  const { user } = useUser();
  const { user: currentUser } = useUser();

  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "Coordinacion");
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Coordinacion" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const [cerrarDialogOpen, setCerrarDialogOpen] = useState(false);
  const [validarDialogOpen, setValidarDialogOpen] = useState(false);
  const [desasignarDialogOpen, setDesasignarDialogOpen] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [fuecDialogOpen, setFuecDialogOpen] = useState(false);
  const [corregirCoordenadasOpen, setCorregirCoordenadasOpen] = useState(false);
  const [origenDestinoIds, setOrigenDestinoIds] = useState(null);
  const [observacionesDialogOpen, setObservacionesDialogOpen] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [confirmarDialogOpen, setConfirmarDialogOpen] = useState(false);
  const [verDialogOpen, setVerDialogOpen] = useState(false);
  const [desconfirmarDialogOpen, setDesconfirmarDialogOpen] = useState(false);
  const [desvalidarDialogOpen, setDesvalidarDialogOpen] = useState(false);
  const [reiniciarDialogOpen, setReiniciarDialogOpen] = useState(false);
  const [rutaGpsDialogOpen, setRutaGpsDialogOpen] = useState(false);

  const ESTADOS_CERRADOS = useMemo(
    () => ["CANCELADO", "FALLIDO", "FINALIZADO"],
    []
  );

  // Estados pendientes de validación (pueden cerrarse pero no están cerrados)
  const ESTADOS_PENDIENTES_VALIDACION = useMemo(
    () => ["FALLIDOXVALIDAR", "FINALIZADOXVALIDAR"],
    []
  );

  const isPendienteValidacion = useMemo(
    () => ESTADOS_PENDIENTES_VALIDACION.includes(traslado.status?.toUpperCase()),
    [traslado.status, ESTADOS_PENDIENTES_VALIDACION]
  );

  const isCerrado = useMemo(
    () => ESTADOS_CERRADOS.includes(traslado.status?.toUpperCase()),
    [traslado.status, ESTADOS_CERRADOS]
  );

  // Verificar si el control de servicio está facturado o recibido (bloqueado para edición)
  const isControlBloqueado = useMemo(() => {
    // Verificar ambas posibles claves (camelCase y snake_case)
    const control = traslado.controlServicio || traslado.control_servicio;
    const estadoAuditoria = control?.estado_auditoria;
    return estadoAuditoria === 'facturado' || estadoAuditoria === 'recibido';
  }, [traslado]);

  // Obtener el label descriptivo para el estado bloqueado
  const estadoBloqueadoLabel = useMemo(() => {
    const control = traslado.controlServicio || traslado.control_servicio;
    const estadoAuditoria = control?.estado_auditoria;
    if (estadoAuditoria === 'facturado') return 'Control Facturado';
    if (estadoAuditoria === 'recibido') return 'Control Recibido';
    return '';
  }, [traslado]);

  // Memoizar estados derivados del traslado
  const { isConfirmed, isValidated, puedeDesasignar } = useMemo(() => {
    const confirmed = traslado.confirmacion === "Confirmado";
    const validated = traslado.seguimiento === "Validado";
    const vehicleAssigned = Boolean(traslado.nombre_vehiculo && traslado.placa);
    const canUnassign = !isCerrado && vehicleAssigned;

    return {
      isConfirmed: confirmed,
      isValidated: validated,
      puedeDesasignar: canUnassign,
    };
  }, [
    traslado.confirmacion,
    traslado.seguimiento,
    traslado.nombre_vehiculo,
    traslado.placa,
    isCerrado,
  ]);

  const [confirmarAsignacionDialogOpen, setConfirmarAsignacionDialogOpen] =
    useState(false);



  const handleSubMenuOptionClick = useCallback(
    (option) => {
      handleOptionClick(option);
      handleCloseContextMenu();
    },
    [handleOptionClick, handleCloseContextMenu]
  );

  const handleOpenCerrarDialog = useCallback(() => {
    setCerrarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseCerrarDialog = useCallback(() => {
    setCerrarDialogOpen(false);
  }, []);

  const handleOpenObservaciones = useCallback(() => {
    setObservacionesDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseObservacionesDialog = useCallback(() => {
    setObservacionesDialogOpen(false);
  }, []);

  const handleOpenHistorial = useCallback(() => {
    setHistorialDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseHistorialDialog = useCallback(() => {
    setHistorialDialogOpen(false);
  }, []);

  const handleOpenConfirmarDialog = useCallback(() => {
    setConfirmarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseConfirmarDialog = useCallback(() => {
    setConfirmarDialogOpen(false);
  }, []);

  const handleOpenVerTrasladoDialog = useCallback(() => {
    setVerDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseVerTrasladoDialog = useCallback(() => {
    setVerDialogOpen(false);
  }, []);

  // Abrir el diálogo de desasignar móvil
  const handleOpenDesasignarDialog = useCallback(() => {
    setDesasignarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Cerrar el diálogo de desasignar móvil
  const handleCloseDesasignarDialog = useCallback(() => {
    setDesasignarDialogOpen(false);
  }, []);

  // Abrir el diálogo de confirmar asignación de vehículo
  const handleOpenConfirmarAsignacionDialog = useCallback(() => {
    setConfirmarAsignacionDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Cerrar el diálogo de confirmar asignación de vehículo
  const handleCloseConfirmarAsignacionDialog = useCallback(() => {
    setConfirmarAsignacionDialogOpen(false);
  }, []);

  const handleOpenDesconfirmarDialog = useCallback(() => {
    setDesconfirmarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseDesconfirmarDialog = useCallback(() => {
    setDesconfirmarDialogOpen(false);
  }, []);

  const handleOpenDesvalidarDialog = useCallback(() => {
    setDesvalidarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseDesvalidarDialog = useCallback(() => {
    setDesvalidarDialogOpen(false);
  }, []);

  const handleConfirmarAsignacion = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/confirmar-asignacion/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Llamar a onEstadoCambiado para actualizar el estado del traslado en el componente padre
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, { status: "Asignado" });
      }

      setConfirmarAsignacionDialogOpen(false);
    } catch (error) {
      console.error("Error al confirmar la asignación:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  // Función para desasignar el móvil y actualizar el estado
  const handleDesasignarMovil = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/desasignar-vehiculo/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Actualizar el estado del traslado
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, {
          vehiculo_id: null,
          nombre_vehiculo: null,
          placa: null,
          conductor_id: null,
          nombre_conductor: null,
          status: "Programado",
        });
      }

      setDesasignarDialogOpen(false);
    } catch (error) {
      console.error("Error al desasignar el móvil:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  // Confirmar traslado y actualizar el estado sin refrescar el navegador
  const handleConfirmarTraslado = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/confirmar/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Llamar a onEstadoCambiado para actualizar el estado del traslado en el componente padre
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, { confirmacion: "Confirmado" });
      }

      setConfirmarDialogOpen(false);
    } catch (error) {
      console.error("Error al confirmar traslado:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  // Manejar el cierre del traslado
  const handleCerrarTraslado = useCallback(
    async (estado, mensaje, yaProcesado = false) => {
      if (!trasladoId || !user) {
        console.error("Error: trasladoId o usuario_id no definidos");
        return;
      }
      try {
        if (!yaProcesado) await api.put(`/traslados/cerrar/${trasladoId}`, {
          usuario_id: user.id,
          estado: estado,
          mensaje: mensaje,
        });
        if (onEstadoCambiado) {
          onEstadoCambiado(trasladoId, { status: estado });
        }
      } catch (error) {
        console.error("Error al cerrar el traslado:", error);
      }
      setCerrarDialogOpen(false);
    },
    [trasladoId, user, onEstadoCambiado]
  );

  const handleOpenValidarDialog = useCallback(() => {
    setValidarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCloseValidarDialog = useCallback(() => {
    setValidarDialogOpen(false);
  }, []);

  const handleDesconfirmarTraslado = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/desconfirmar/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Actualizar el estado del traslado
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, { confirmacion: "Sin Confirmar" });
      }

      setDesconfirmarDialogOpen(false);
    } catch (error) {
      console.error("Error al desconfirmar traslado:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  const handleDesvalidarTraslado = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/desvalidar/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Actualizar el estado del traslado
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, { seguimiento: "Sin Validar" });
      }

      setDesvalidarDialogOpen(false);
    } catch (error) {
      console.error("Error al desvalidar traslado:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  const handleValidarTraslado = useCallback(async () => {
    if (!trasladoId || !user) {
      console.error("Error: trasladoId o usuario_id no definidos");
      return;
    }

    try {
      await api.put(`/traslados/validar/${trasladoId}`, {
        usuario_id: user.id,
      });

      // Actualizar el estado del traslado
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, { seguimiento: "Validado" });
      }

      setValidarDialogOpen(false);
    } catch (error) {
      console.error("Error al validar traslado:", error);
    }
  }, [trasladoId, user, onEstadoCambiado]);

  // Abrir el diálogo de mapa para ver la ruta
  const handleVerRuta = useCallback(() => {
    if (
      !origin?.lat ||
      !origin?.lng ||
      !destination?.lat ||
      !destination?.lng
    ) {
      message.error("No hay coordenadas disponibles para mostrar la ruta");
      handleCloseContextMenu();
      return;
    }
    setMapVisible(true);
    handleCloseContextMenu();
  }, [origin, destination, handleCloseContextMenu]);

  // Cerrar el diálogo de mapa
  const handleCloseMap = useCallback(() => {
    setMapVisible(false);
  }, []);

  // Handler para abrir diálogo de reinicio
  const handleOpenReiniciarDialog = useCallback(() => {
    setReiniciarDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Handler para cerrar diálogo de reinicio
  const handleCloseReiniciarDialog = useCallback(() => {
    setReiniciarDialogOpen(false);
  }, []);

  // Handler para abrir diálogo de Ruta GPS
  const handleOpenRutaGpsDialog = useCallback(() => {
    setRutaGpsDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Ver FUEC del traslado (R10)
  const handleOpenFuec = useCallback(() => {
    setFuecDialogOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Corregir coordenadas A/B (R12): cualquier estado, salvo control recibido o facturado
  const handleOpenCorregirCoordenadas = useCallback(() => {
    setCorregirCoordenadasOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Actualizar origen - destino: los seleccionados si el traslado del clic está entre ellos; si no, solo este
  const idsOrigenDestino = useMemo(
    () =>
      selectedTraslados?.length > 0 && selectedTraslados.includes(trasladoId) ? selectedTraslados : [trasladoId],
    [selectedTraslados, trasladoId]
  );
  const handleOpenOrigenDestino = useCallback(() => {
    setOrigenDestinoIds(idsOrigenDestino);
    handleCloseContextMenu();
  }, [idsOrigenDestino, handleCloseContextMenu]);

  const handleCoordenadasCorregidas = useCallback(
    (coordenadas) => {
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, coordenadas);
      }
    },
    [onEstadoCambiado, trasladoId]
  );

  // Handler para cerrar diálogo de Ruta GPS
  const handleCloseRutaGpsDialog = useCallback(() => {
    setRutaGpsDialogOpen(false);
  }, []);

  // Handler para confirmar reinicio
  const handleReiniciarTraslado = useCallback(
    async (trasladoId, motivo) => {
      try {
        await api.put(`/centro-control/traslados/${trasladoId}/reiniciar`, {
          usuario_id: user.id,
          motivo_reinicio: motivo,
        });

        // Actualizar estado local
        if (onEstadoCambiado) {
          onEstadoCambiado(trasladoId, {
            status: "Programado",
            vehiculo_id: null,
            nombre_vehiculo: null,
            placa: null,
            conductor_id: null,
            nombre_conductor: null,
            seguimiento: "Sin Validar",
            confirmacion: "Sin Confirmar",
            motivo_reinicio: motivo,
          });
        }
      } catch (error) {
        console.error("Error al reiniciar traslado:", error);
      } finally {
        setReiniciarDialogOpen(false);
      }
    },
    [user, onEstadoCambiado]
  );

  // Estilos pre-computados para mejor performance
  const menuStyles = useMemo(
    () => ({
      menuItem: {
        padding: "10px 12px",
        borderRadius: "4px",
        margin: "1px 0",
        minHeight: "40px",
        lineHeight: "20px",
      },
      subMenuItem: {
        padding: "10px 12px",
        borderRadius: "4px",
        margin: "1px 0",
        minHeight: "38px",
        lineHeight: "18px",
      },
      iconDefault: { color: "#0a2e5c", fontSize: "16px" },
      iconDanger: { color: "#f5222d", fontSize: "16px" },
      textDefault: { color: "#0a2e5c", fontWeight: 500, fontSize: "14px", whiteSpace: "nowrap", overflow: "visible" },
      textDanger: { color: "#f5222d", fontWeight: 500, fontSize: "14px", whiteSpace: "nowrap", overflow: "visible" },
      textDisabled: { color: "#bfbfbf", fontWeight: 500, fontSize: "14px", whiteSpace: "nowrap", overflow: "visible" },
    }),
    []
  );

  // Función auxiliar optimizada para crear items del menú
  const createMenuItem = useCallback(
    (key, icon, label, onClick, disabled = false, danger = false) => {
      const iconStyle = danger ? menuStyles.iconDanger : menuStyles.iconDefault;
      const textStyle = disabled
        ? menuStyles.textDisabled
        : danger
        ? menuStyles.textDanger
        : menuStyles.textDefault;

      return {
        key,
        icon: <div style={iconStyle}>{icon}</div>,
        label: <Typography.Text style={textStyle} ellipsis={false}>{label}</Typography.Text>,
        onClick: disabled ? undefined : onClick,
        disabled,
        style: menuStyles.menuItem,
      };
    },
    [menuStyles]
  );

  // Función auxiliar optimizada para crear submenú
  const createSubMenuItem = useCallback(
    (key, icon, label, children) => ({
      key,
      icon: <div style={menuStyles.iconDefault}>{icon}</div>,
      label: (
        <Typography.Text style={menuStyles.textDefault}>
          {label}
        </Typography.Text>
      ),
      children: children.map((child) => ({
        ...child,
        style: menuStyles.subMenuItem,
      })),
      style: menuStyles.menuItem,
    }),
    [menuStyles]
  );

  // Pre-computar las opciones del submenú de teléfonos (dinámico con paciente y responsables)
  const phoneSubMenuItems = useMemo(() => {
    const items = [];

    // Teléfono del paciente
    if (traslado?.numero_contacto_paciente) {
      items.push(
        createMenuItem(
          "tel-paciente",
          <PhoneOutlined />,
          `Paciente: ${traslado.numero_contacto_paciente}`,
          () => handleSubMenuOptionClick("copyPhone")
        )
      );
    }

    // Teléfonos de responsables/acompañantes
    const responsables = traslado?.paciente?.responsables || [];
    responsables.forEach((resp, index) => {
      if (resp.telefono) {
        const label = resp.parentesco
          ? `${resp.parentesco}: ${resp.telefono}`
          : resp.telefono;
        items.push(
          createMenuItem(
            `tel-responsable-${index}`,
            <PhoneOutlined />,
            label,
            () => handleSubMenuOptionClick(`copyResponsiblePhone:${resp.telefono}`)
          )
        );
      }
    });

    // Si no hay responsables en el array, mostrar el campo legacy
    if (responsables.length === 0 && traslado?.numero_contacto_responsable) {
      items.push(
        createMenuItem(
          "tel-responsable",
          <PhoneOutlined />,
          traslado.numero_contacto_responsable,
          () => handleSubMenuOptionClick("copyResponsiblePhone")
        )
      );
    }

    // Si no hay teléfonos disponibles
    if (items.length === 0) {
      items.push(
        createMenuItem(
          "tel-none",
          <PhoneOutlined />,
          "Sin teléfonos disponibles",
          () => {},
          true // disabled
        )
      );
    }

    return items;
  }, [createMenuItem, handleSubMenuOptionClick, traslado]);

  // Crear items del menú principal con optimización
  const menuItems = useMemo(() => {
    const items = [];

    // 1) Ver Traslado
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createMenuItem(
          "ver-traslado",
          <EyeOutlined />,
          "Ver Traslado",
          handleOpenVerTrasladoDialog
        )
      );
    }

    // 2) Reiniciar Traslado
    if (hasPermission("REINICIAR_CO")) {
      const isDisabled = !isCerrado || isControlBloqueado;
      items.push(
        createMenuItem(
          "reiniciar-traslado",
          <ReloadOutlined />,
          isControlBloqueado ? `Reiniciar Traslado (${estadoBloqueadoLabel})` : "Reiniciar Traslado",
          handleOpenReiniciarDialog,
          isDisabled
        )
      );
    }

    // 3) Submenú Teléfonos
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createSubMenuItem(
          "telefonos",
          <PhoneOutlined />,
          "Copiar teléfonos",
          phoneSubMenuItems
        )
      );
    }

    // 4) Confirmar/Desconfirmar
    if (hasPermission("CONFIRMACION_CO")) {
      items.push(
        isConfirmed
          ? createMenuItem(
              "desconfirmar",
              <CloseOutlined />,
              "Desconfirmar traslado",
              handleOpenDesconfirmarDialog
            )
          : createMenuItem(
              "confirmar",
              <CheckOutlined />,
              "Confirmar traslado",
              handleOpenConfirmarDialog
            )
      );
    }

    // 5) Ver ruta
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createMenuItem(
          "ver-ruta",
          <EnvironmentOutlined />,
          "Ver ruta",
          handleVerRuta
        )
      );
    }

    // 6) Ver Ruta GPS
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createMenuItem(
          "ver-ruta-gps",
          <AimOutlined />,
          "Ver Ruta GPS",
          handleOpenRutaGpsDialog
        )
      );
    }

    // Ver FUEC del traslado
    if (hasPermission("VER_FUEC_CO")) {
      items.push(
        createMenuItem(
          "ver-fuec",
          <FileTextOutlined />,
          traslado.fuec_id ? "Ver FUEC" : "Ver FUEC (sin FUEC)",
          handleOpenFuec,
          !traslado.fuec_id
        )
      );
    }

    // Corregir coordenadas de origen (A) y destino (B)
    if (hasPermission("CORREGIR_COORDENADAS_CO")) {
      items.push(
        createMenuItem(
          "corregir-coordenadas",
          <PushpinOutlined />,
          isControlBloqueado ? `Corregir coordenadas (${estadoBloqueadoLabel})` : "Corregir coordenadas",
          handleOpenCorregirCoordenadas,
          isControlBloqueado
        )
      );
    }

    // Actualizar origen - destino desde la autorización (individual o con los seleccionados)
    if (hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CO")) {
      items.push(
        createMenuItem(
          "actualizar-origen-destino",
          <SwapOutlined />,
          idsOrigenDestino.length > 1
            ? `Actualizar origen - destino (${idsOrigenDestino.length} seleccionados)`
            : "Actualizar origen - destino",
          handleOpenOrigenDestino
        )
      );
    }

    // 7) Historial
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createMenuItem(
          "historial",
          <HistoryOutlined />,
          "Ver Historial",
          handleOpenHistorial
        )
      );
    }

    // 8) Observaciones
    if (hasPermission("VISUALIZAR_CO")) {
      items.push(
        createMenuItem(
          "observaciones",
          <CommentOutlined />,
          "Observaciones",
          handleOpenObservaciones
        )
      );
    }

    // 9) Validar/Desvalidar
    if (hasPermission("VALIDACION_CO")) {
      items.push(
        isValidated
          ? createMenuItem(
              "desvalidar",
              <CloseOutlined />,
              "Desvalidar traslado",
              handleOpenDesvalidarDialog
            )
          : createMenuItem(
              "validar",
              <SafetyCertificateOutlined />,
              "Validar traslado",
              handleOpenValidarDialog
            )
      );
    }

    // 10) Cerrar traslado (también disponible para estados Xvalidar)
    if (hasPermission("CERRAR_CO")) {
      items.push(
        createMenuItem(
          "cerrar",
          <CloseOutlined />,
          isCerrado ? "Traslado Cerrado" : "Cerrar Traslado",
          handleOpenCerrarDialog,
          isCerrado, // Deshabilitado solo si está realmente cerrado
          !isCerrado || isPendienteValidacion // Mostrar punto si no está cerrado o está pendiente validación
        )
      );
    }

    // 11) Desasignar Móvil
    if (hasPermission("ASIGNAR_CO")) {
      items.push(
        createMenuItem(
          "desasignar",
          <CloseOutlined />,
          "Desasignar Móvil",
          handleOpenDesasignarDialog,
          !puedeDesasignar
        )
      );
    }

    // 12) Asignar móvil
    if (hasPermission("ASIGNAR_CO")) {
      items.push(
        createMenuItem(
          "asignar-movil",
          <CarOutlined />,
          "Asignar móvil",
          handleOpenConfirmarAsignacionDialog,
          traslado.status !== "Pre-Asignado"
        )
      );
    }

    // 13) Asignar/Pre-Asignar Traslados (múltiple)
    if (hasPermission("PREASIGNAR_CC")) {
      const isMultiAssignDisabled =
        (selectedStatus === "Programado" && !selectedMovil) ||
        !["Programado", "Pre-Asignado"].includes(selectedStatus) ||
        selectedTraslados.length === 0;

      items.push(
        createMenuItem(
          "asignar-multiple",
          <SafetyCertificateOutlined />,
          selectedStatus === "Pre-Asignado"
            ? "Asignar Traslados"
            : "Pre-Asignar Traslados",
          () => {
            handleCloseContextMenu();
            onAsignarMultiple(selectedTraslados, selectedStatus);
          },
          isMultiAssignDisabled
        )
      );
    }

    return items;
  }, [
    hasPermission,
    isCerrado,
    isConfirmed,
    isValidated,
    puedeDesasignar,
    isControlBloqueado,
    estadoBloqueadoLabel,
    selectedStatus,
    selectedMovil,
    selectedTraslados,
    createMenuItem,
    createSubMenuItem,
    phoneSubMenuItems,
    handleOpenVerTrasladoDialog,
    handleOpenReiniciarDialog,
    handleOpenDesconfirmarDialog,
    handleOpenConfirmarDialog,
    handleVerRuta,
    handleOpenRutaGpsDialog,
    handleOpenFuec,
    handleOpenCorregirCoordenadas,
    idsOrigenDestino,
    handleOpenOrigenDestino,
    handleOpenHistorial,
    handleOpenObservaciones,
    handleOpenDesvalidarDialog,
    handleOpenValidarDialog,
    handleOpenCerrarDialog,
    handleOpenDesasignarDialog,
    handleOpenConfirmarAsignacionDialog,
    handleCloseContextMenu,
    onAsignarMultiple,
    traslado.status,
    traslado.fuec_id,
  ]);

  // Configuración del menú memoizada
  const menuConfig = useMemo(
    () => ({
      width: 260,
      itemHeight: 42,
      padding: 16,
      margin: 15,
      offset: 5,
    }),
    []
  );

  // Función optimizada para calcular la posición inteligente del menú
  const getSmartMenuPosition = useCallback(
    (mouseX, mouseY) => {
      const menuHeight =
        menuItems.length * menuConfig.itemHeight + menuConfig.padding;
      const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

      // Calcular posición X (horizontal)
      let adjustedX = mouseX + menuConfig.offset;
      if (adjustedX + menuConfig.width > windowWidth - menuConfig.margin) {
        adjustedX = mouseX - menuConfig.width - menuConfig.offset;
      }
      if (adjustedX < menuConfig.margin) {
        adjustedX = menuConfig.margin;
      }
      if (adjustedX + menuConfig.width > windowWidth - menuConfig.margin) {
        adjustedX = Math.max(
          menuConfig.margin,
          (windowWidth - menuConfig.width) / 2
        );
      }

      // Calcular posición Y (vertical)
      let adjustedY = mouseY + menuConfig.offset;
      if (adjustedY + menuHeight > windowHeight - menuConfig.margin) {
        adjustedY = mouseY - menuHeight - menuConfig.offset;
      }
      if (adjustedY < menuConfig.margin) {
        adjustedY = menuConfig.margin;
      }
      if (adjustedY + menuHeight > windowHeight - menuConfig.margin) {
        adjustedY = Math.max(
          menuConfig.margin,
          (windowHeight - menuHeight) / 2
        );
      }

      return {
        x: Math.round(adjustedX),
        y: Math.round(adjustedY),
        needsScroll: menuHeight > windowHeight - 2 * menuConfig.margin,
      };
    },
    [menuItems.length, menuConfig]
  );

  // Calcular la posición inteligente del menú
  const smartPosition = useMemo(
    () =>
      contextMenu
        ? getSmartMenuPosition(contextMenu.mouseX, contextMenu.mouseY)
        : null,
    [contextMenu, getSmartMenuPosition]
  );

  // Estilos CSS memoizados para mejor performance
  const contextMenuStyles = useMemo(
    () => `
    .mogotax-context-menu-coordinacion::-webkit-scrollbar {
      width: 6px;
    }
    .mogotax-context-menu-coordinacion::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 3px;
    }
    .mogotax-context-menu-coordinacion::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 3px;
    }
    .mogotax-context-menu-coordinacion::-webkit-scrollbar-thumb:hover {
      background: #bfbfbf;
    }
    .mogotax-context-menu-coordinacion .ant-menu-item:hover,
    .mogotax-context-menu-coordinacion .ant-menu-submenu-title:hover {
      background-color: #e6f7ff !important;
      color: #0a2e5c !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-item-disabled {
      color: #bfbfbf !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu-title {
      padding: 10px 12px !important;
      margin: 1px 0 !important;
      border-radius: 4px !important;
      min-height: 40px !important;
      line-height: 20px !important;
      display: flex !important;
      align-items: center !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu-title:after {
      content: "▶" !important;
      font-size: 10px !important;
      color: #8c8c8c !important;
      margin-left: auto !important;
      margin-right: 0 !important;
      transform: none !important;
      transition: transform 0.2s ease !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu-open .ant-menu-submenu-title:after {
      transform: rotate(90deg) !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu-arrow {
      display: none !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu {
      z-index: 10003 !important;
      position: relative !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-submenu .ant-menu-submenu-popup {
      z-index: 10003 !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-sub {
      z-index: 10003 !important;
      box-shadow: 0 6px 24px rgba(10, 46, 92, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
      border: 1px solid #e8e8e8 !important;
      border-radius: 8px !important;
      background-color: #ffffff !important;
      backdrop-filter: blur(10px) !important;
      min-width: 280px !important;
      max-width: 400px !important;
      padding: 8px !important;
      position: fixed !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-sub .ant-menu-title-content {
      white-space: nowrap !important;
      overflow: visible !important;
      text-overflow: unset !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-sub .ant-menu-item {
      padding: 10px 12px !important;
      margin: 1px 0 !important;
      border-radius: 4px !important;
      min-height: 38px !important;
      line-height: 18px !important;
    }
    .mogotax-context-menu-coordinacion .ant-menu-sub .ant-menu-item:hover {
      background-color: #e6f7ff !important;
      color: #0a2e5c !important;
    }
    .ant-menu-submenu-popup {
      z-index: 10003 !important;
    }
    body > .ant-menu-submenu-popup {
      z-index: 10003 !important;
      position: fixed !important;
    }
  `,
    []
  );

  // Inyectar estilos optimizados
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-menu-coordinacion", "true");
    style.textContent = contextMenuStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [contextMenuStyles]);

  const allowedCloseStates = useMemo(() => {
    const status = traslado.status;
    if (status === "Programado") {
      return ["Cancelado"];
    }
    if (status === "FallidoXvalidar") {
      // FallidoXvalidar solo puede ir a Fallido o Cancelado
      return ["Finalizado", "Cancelado", "Fallido"];
    }
    if (status === "FinalizadoXvalidar") {
      // FinalizadoXvalidar puede ir a Finalizado (con OTP), Fallido o Cancelado
      return ["Finalizado", "Fallido", "Cancelado"];
    }
    return ["Finalizado", "Cancelado", "Fallido"];
  }, [traslado.status]);

  return (
    <>
      {/* === Menú Contextual Principal con Ant Design === */}
      {contextMenu && smartPosition && (
        <div
          style={{
            position: "fixed",
            top: smartPosition.y,
            left: smartPosition.x,
            zIndex: 10000,
          }}
        >
          <Menu
            style={{
              borderRadius: "8px",
              boxShadow:
                "0 6px 24px rgba(10, 46, 92, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e8e8e8",
              backgroundColor: "#ffffff",
              backdropFilter: "blur(10px)",
              minWidth: "240px",
              maxWidth: "280px",
              padding: "8px",
              maxHeight: smartPosition?.needsScroll
                ? "calc(100vh - 30px)"
                : "auto",
              overflowY: smartPosition?.needsScroll ? "auto" : "visible",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              zIndex: 10000,
            }}
            className="mogotax-context-menu-coordinacion"
            mode="vertical"
            items={menuItems}
            onClick={handleCloseContextMenu}
            getPopupContainer={() => document.body}
          />
        </div>
      )}

      {/* Overlay para cerrar el menú al hacer clic fuera */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: "transparent",
          }}
          onClick={handleCloseContextMenu}
        />
      )}

      {/* Diálogo para cerrar traslado */}
      {hasPermission("CERRAR_CO") && cerrarDialogOpen && (
        <CerrarTrasladoDialog
          open={cerrarDialogOpen}
          onClose={handleCloseCerrarDialog}
          onSubmit={handleCerrarTraslado}
          allowedStates={allowedCloseStates}
          trasladoId={trasladoId}
          isTAI={traslado.es_tai || false}
          estadoActualTraslado={traslado.status}
        />
      )}

      {/* Diálogo para validar traslado */}
      {hasPermission("VALIDACION_CO") && validarDialogOpen && (
        <ValidarTrasladoDialog
          open={validarDialogOpen}
          onClose={handleCloseValidarDialog}
          onSubmit={handleValidarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Desconfirmar Traslado */}
      {hasPermission("CONFIRMACION_CO") && desconfirmarDialogOpen && (
        <DesconfirmarTrasladoDialog
          open={desconfirmarDialogOpen}
          onClose={handleCloseDesconfirmarDialog}
          onDesconfirmar={handleDesconfirmarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Desvalidar Traslado */}
      {hasPermission("VALIDACION_CO") && desvalidarDialogOpen && (
        <DesvalidarTrasladoDialog
          open={desvalidarDialogOpen}
          onClose={handleCloseDesvalidarDialog}
          onDesvalidar={handleDesvalidarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Confirmar Traslado */}
      {hasPermission("CONFIRMACION_CO") && confirmarDialogOpen && (
        <ConfirmarTrasladoDialog
          open={confirmarDialogOpen}
          onClose={handleCloseConfirmarDialog}
          onConfirm={handleConfirmarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Confirmar Asignación de Vehículo */}
      {hasPermission("ASIGNAR_CO") && confirmarAsignacionDialogOpen && (
        <AsignarMovilDialog
          open={confirmarAsignacionDialogOpen}
          onClose={handleCloseConfirmarAsignacionDialog}
          onConfirm={handleConfirmarAsignacion}
          trasladoId={trasladoId}
          nombreVehiculo={traslado.nombre_vehiculo}
          placa={traslado.placa}
        />
      )}

      {/* Diálogo para Desasignar Móvil */}
      {hasPermission("ASIGNAR_CO") && desasignarDialogOpen && (
        <DesasignarMovilDialog
          open={desasignarDialogOpen}
          onClose={handleCloseDesasignarDialog}
          onConfirm={handleDesasignarMovil}
          trasladoId={trasladoId}
          nombreVehiculo={traslado.nombre_vehiculo}
          placa={traslado.placa}
        />
      )}

      {/* Diálogo para Ver Traslado */}
      {hasPermission("VISUALIZAR_CO") && verDialogOpen && (
        <VerTrasladoDialog
          open={verDialogOpen}
          onClose={handleCloseVerTrasladoDialog}
          traslado={traslado}
        />
      )}

      {/* Diálogo para Ver Observaciones */}
      {hasPermission("VISUALIZAR_CO") && observacionesDialogOpen && (
        <ObservacionesDialog
          open={observacionesDialogOpen}
          onClose={handleCloseObservacionesDialog}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Ver Historial */}
      {hasPermission("VISUALIZAR_CO") && historialDialogOpen && (
        <HistorialTrasladoDialog
          open={historialDialogOpen}
          onClose={handleCloseHistorialDialog}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* Diálogo para Ver Ruta en Mapa */}
      {hasPermission("VISUALIZAR_CO") &&
        origin &&
        destination &&
        mapVisible && (
          <MapDialog
            open={mapVisible}
            onClose={handleCloseMap}
            origin={origin}
            destination={destination}
            traslado={traslado}
          />
        )}

      {/* Diálogo de reinicio */}
      {hasPermission("REINICIAR_CO") && (
        <ReiniciarTrasladoDialog
          open={reiniciarDialogOpen}
          onClose={handleCloseReiniciarDialog}
          trasladoId={trasladoId.toString()}
          onConfirm={handleReiniciarTraslado}
        />
      )}

      {/* Ver FUEC del traslado */}
      {hasPermission("VER_FUEC_CO") && fuecDialogOpen && (
        <FuecTrasladoDialog
          open={fuecDialogOpen}
          fuecId={traslado.fuec_id}
          onClose={() => setFuecDialogOpen(false)}
          canDownload={hasPermission("DESCARGAR_FUEC_CO")}
        />
      )}

      {/* Corregir coordenadas */}
      {hasPermission("CORREGIR_COORDENADAS_CO") && corregirCoordenadasOpen && (
        <CorregirCoordenadasDialog
          open={corregirCoordenadasOpen}
          onClose={() => setCorregirCoordenadasOpen(false)}
          traslado={traslado}
          onCorregido={handleCoordenadasCorregidas}
        />
      )}

      {hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CO") && origenDestinoIds && (
        <ActualizarOrigenDestinoDialog
          open
          onClose={() => setOrigenDestinoIds(null)}
          trasladoIds={origenDestinoIds}
          modulo="Coordinacion"
          onActualizado={onOrigenDestinoActualizado}
        />
      )}

      {/* Diálogo para Ver Ruta GPS */}
      {hasPermission("VISUALIZAR_CO") && (
        <RutaTrazadaView
          visible={rutaGpsDialogOpen}
          trasladoId={trasladoId}
          traslado={traslado}
          onClose={handleCloseRutaGpsDialog}
        />
      )}
    </>
  );
}

// Validación de las props con PropTypes
MenuContextualTraslado.propTypes = {
  contextMenu: PropTypes.shape({
    mouseX: PropTypes.number,
    mouseY: PropTypes.number,
  }),
  handleCloseContextMenu: PropTypes.func.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onEstadoCambiado: PropTypes.func,
  origin: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  destination: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  traslado: PropTypes.shape({
    confirmacion: PropTypes.string.isRequired,
    seguimiento: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    nombre_vehiculo: PropTypes.string,
    placa: PropTypes.string,
    es_tai: PropTypes.bool,
  }).isRequired,
  selectedTraslados: PropTypes.array,
  onAsignarMultiple: PropTypes.func,
  onOrigenDestinoActualizado: PropTypes.func,
  selectedMovil: PropTypes.object,
  selectedStatus: PropTypes.string,
};

export default MenuContextualTraslado;
